import { useQuery } from "@apollo/client"
import { Dispatch, FC, SetStateAction } from "react"
import { gql } from "~/__generated__"
import { cn } from "~/common/cn"
import { DelayedLoadingSpinner } from "./delayed-loading-spinner"

type GroupTabItemProps = {
  name: string
  count: number
  isActive: boolean
  description?: string | null
  onClick: () => void
}

const GroupTabItem: FC<GroupTabItemProps> = ({ name, count, isActive, description, onClick }) => (
  <div
    title={description || ""}
    className={cn(
      "flex flex-shrink-0 pb-4 text-sm",
      isActive ? "border-b border-black text-black" : "cursor-pointer text-gray-400"
    )}
    onClick={onClick}
  >
    <div className="">{name}</div>
    <span
      className={cn(
        "ml-2 inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium",
        isActive ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-600"
      )}
    >
      {count}
    </span>
  </div>
)

const groupsQuery = gql(/* GraphQL */ `
  query GroupsFilters {
    groups(first: 100, filters: { preset: MEMBER, sort: RECENTLY_USED }) {
      nodes {
        id
        description
        name
        bookmarksCount
        bookmarksVisibleCount
      }
    }
  }
`)

const GroupsFilter: FC<{
  groupFilter: string | null
  setGroupFilter: Dispatch<SetStateAction<string | null>>
  bookmarksCount: number | undefined
}> = ({ groupFilter, setGroupFilter, bookmarksCount }) => {
  const { data: groupsData, loading: groupsLoading } = useQuery(groupsQuery)
  const groups = groupsData?.groups.nodes.filter((group) => group.bookmarksVisibleCount > 0)

  return groupsLoading ? (
    <DelayedLoadingSpinner />
  ) : groups?.length && groups.length > 0 ? (
    <div className="mt-6 flex flex-wrap gap-x-10 gap-y-2 border-b">
      <GroupTabItem
        name="All"
        count={bookmarksCount || 0}
        isActive={groupFilter == null}
        onClick={() => setGroupFilter(null)}
      />
      {groups.map((group) => (
        <GroupTabItem
          key={group.id}
          name={group.name}
          count={group.bookmarksVisibleCount}
          isActive={groupFilter === group.id}
          description={group.description}
          onClick={() => setGroupFilter(group.id)}
        />
      ))}
    </div>
  ) : null
}

export default GroupsFilter
